import React, { useContext, useState } from 'react'

import { AcornsIcon } from '@acorns/icons'
import { Spinner } from '@acorns/web-components'
import { useApolloErrorModal, useRemoteStrings } from '@acorns/web-utils'
import { useLazyQuery, useMutation } from '@apollo/react-hooks'

import { CTAContainer } from 'components/CTAContainer'
import Layout from 'components/Layout'
import Separator from 'components/Separator'
import { User } from 'generated/types'
import { getGeneralError } from 'utils/getGeneralError'

import { emptyAddress } from '../constants'
import VerifyDeliveryAddress from '../gql/address-validation-query.gql'
import UpdateShippingInfo from '../gql/update-shipping-address.gql'
import {
  AddressBody,
  AddressCard,
  AddressCardContainer,
  AddressHeader,
  EditIcon,
  HeroHeader,
  HeroText,
} from '../shared-components'
import { AddressType, ShippingAddressContext } from '../ShippingAddressContext'
import { ShippingSubRoutes } from '../types'
import { pickAddress } from '../utils'
import {
  addressDeliverabilityUnavailable,
  addressVerificationError,
  addressVerified,
  correctedAddress,
} from '../validation-helpers'
import { useAddressPreviewTracking } from './events'
import { StyledButton } from './styled-components'

type AddressPreviewProps = {
  user: User
  handleNext: () => void
}

export const AddressPreview = ({ user, handleNext }: AddressPreviewProps) => {
  const { getRemoteString } = useRemoteStrings()
  const { addresses, setAddress, didEdit, onEdited, show } = useContext(ShippingAddressContext)
  const [submitting, setSubmitting] = useState(false)
  const [updateShippingAddress] = useMutation(UpdateShippingInfo)
  const address = pickAddress(addresses.residential, addresses.mailing)
  const track = useAddressPreviewTracking()
  const { closeErrorModal, showErrorModal } = useApolloErrorModal()

  const onSubmit = () => {
    track.confirmTapped()
    setSubmitting(true)
    if (didEdit) {
      handleNext()
    } else {
      verifyAddress()
    }
  }

  const onEdit = () => {
    track.editTapped()
    show(ShippingSubRoutes.Edit)
  }

  const [verifyAddress, { data: verifyAddressData }] = useLazyQuery(VerifyDeliveryAddress, {
    fetchPolicy: 'network-only',
    variables: {
      street1: address.street1,
      street2: address.street2,
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
      country: 'US',
    },
    onCompleted: () => {
      setSubmitting(false)
      const verifyAddressResult = verifyAddressData.addressDeliverability
      if (addressVerified(verifyAddressResult)) {
        updateShippingAddress({
          variables: {
            street1: address.street1,
            street2: address.street2,
            city: address.city,
            state: address.state,
            zipCode: address.zipCode,
          },
        }).then(() => {
          setAddress(AddressType.mailing, address)
          onEdited()
          handleNext()
        })
      } else if (correctedAddress(verifyAddressResult)) {
        setAddress(AddressType.unverified, address)
        setAddress(AddressType.corrected, {
          ...emptyAddress,
          ...verifyAddressResult,
        })
        show(ShippingSubRoutes.Validation)
      } else if (addressVerificationError(verifyAddressResult)) {
        setAddress(AddressType.unverified, address)
        show(ShippingSubRoutes.Error)
      } else if (addressDeliverabilityUnavailable(verifyAddressResult)) {
        const { title, message, actions, image } = getGeneralError(
          'Service unavailable',
          { closeErrorModal },
          () => {
            closeErrorModal()
            onSubmit()
          },
          500,
        )
        showErrorModal({ title, message, actions, image })
        setSubmitting(false)
      }
    },
    onError: _error => {
      setSubmitting(false)
    },
  })

  return (
    <Layout header={'heroText.header'} text={'heroText.text'}>
      <HeroHeader>
        {getRemoteString({ key: 'registration.moc_tier.shipping_address.header' })}
      </HeroHeader>
      <HeroText>{getRemoteString({ key: 'registration.shipping_address.body' })}</HeroText>
      <AddressCard onClick={onEdit}>
        <AddressCardContainer>
          <AddressHeader>
            {getRemoteString({ key: 'registration.shipping_address.title' })}
          </AddressHeader>
          <AddressBody>
            {user.firstName} {user.lastName}
          </AddressBody>
          <AddressBody>{address.street1}</AddressBody>
          {address.street2.length > 0 && <AddressBody>{address.street2}</AddressBody>}
          <AddressBody>
            {address.city ? `${address.city},` : ''} {address.state} {address.zipCode}
          </AddressBody>
        </AddressCardContainer>
        <EditIcon icon={AcornsIcon.Icon.UtilityEdit} width={24} height={24} />
      </AddressCard>
      <Separator />
      <CTAContainer>
        <StyledButton disabled={!address.street1 || submitting} onPress={onSubmit}>
          {submitting ? (
            <Spinner height={20} width={20} white />
          ) : (
            getRemoteString({ key: 'registration.shipping_address.cta' })
          )}
        </StyledButton>
      </CTAContainer>
    </Layout>
  )
}
